import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import React, { useEffect, useState } from 'react'
import { Sorting, SortingDirection } from 'sorting'
import { Cell, HeaderCellProps, TableRowData, TableSortMode } from 'tableTypes'
import { v4 as uuidv4 } from 'uuid'
import { useDriverTableStyles } from 'pages/report/molecularBiomarker/MolecularBiomarkersTableRow.styles'
import { useTableWithGenomateScoreColumnStyles } from './TableWithGenomateScoreColumn.styles'
import HeaderCell, { useHeaderCellStyles } from './elements/HeaderCell'
import SimpleCell from './elements/SimpleCell'
import { sortNumberColumn, sortTextColumn } from '../../utils/tableUtils'

type GenomateScoreCellProps = {
    cell: Cell
    children: any
}

enum GenomateScoreOptions {
    gds = 'gds',
    gts = 'gts',
    gbs = 'gbs'
}

const GenomateScoreCell = ({ cell, children }: GenomateScoreCellProps) => (
    <span>
        {React.Children.map(children, (child) =>
            React.cloneElement(child, {
                current: cell.text,
                negative: cell.isNegative
            })
        )}
    </span>
)

type TableWithGenomateScoreColumnProps = {
    header: {
        cells: Array<HeaderCellProps>
        translationNamespace: string
        translationKey: string
    }

    tableRows: Array<TableRowData>
    children: React.ReactNode
}

const getSortingDirectionToggle = (currentDirection: SortingDirection) =>
    currentDirection === 'ASC' ? 'DESC' : 'ASC'

const getSortingProperty = (cells: HeaderCellProps[]) => {
    return cells
        .map((c) => c.id)
        .filter((id) => Object.keys(GenomateScoreOptions).includes(id))[0]
}

const TableWithGenomateScoreColumn = <SortableColumnNames,>({
    header: { cells, translationKey, translationNamespace },
    tableRows,
    children
}: TableWithGenomateScoreColumnProps) => {
    const { chevronCell, variantNoNav } = useDriverTableStyles()
    const { headerCell } = useHeaderCellStyles()
    const styles = useTableWithGenomateScoreColumnStyles()
    const [sorting, setSorting] = useState<Sorting<SortableColumnNames>>(() => {
        const sortingProperty = getSortingProperty(cells)
        return {
            property: sortingProperty as unknown as SortableColumnNames,
            direction: 'DESC'
        }
    })

    const [sortedTableRows, setSortedTableRows] = useState<Array<TableRowData>>(
        []
    )

    const handleSorting = (column: SortableColumnNames) => {
        const newSorting = {
            property: column,
            direction:
                sorting.property === column
                    ? getSortingDirectionToggle(sorting.direction)
                    : ('DESC' as SortingDirection)
        }

        setSorting(newSorting)
    }

    useEffect(() => {
        let sorted
        const sortIndex = cells.findIndex(
            (c) => c.id === (sorting.property as unknown as string)
        )
        const sortBy = cells[sortIndex]

        if (sortBy.type === 'string') {
            sorted = sortTextColumn(
                tableRows,
                sorting.direction as TableSortMode,
                sortIndex
            )
        } else if (sortBy.type === 'number') {
            sorted = sortNumberColumn(
                tableRows,
                sorting.direction as TableSortMode,
                sortIndex
            )
        } else {
            sorted = [...tableRows]
        }

        setSortedTableRows(sorted)
    }, [tableRows, sorting, cells])

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell key="fill-column" className={headerCell}>
                        {''}
                    </TableCell>
                    {cells.map((cell) => (
                        <HeaderCell
                            key={cell.id}
                            cell={cell}
                            translationKey={translationKey}
                            translationNamespace={translationNamespace}
                            handleSorting={handleSorting}
                            sorting={sorting}
                        />
                    ))}
                </TableRow>
            </TableHead>

            <TableBody>
                {sortedTableRows.map((row) => (
                    <TableRow key={uuidv4()}>
                        <TableCell className={chevronCell} />

                        {row.cells.map((cell, cellIndex: number) => {
                            const isFirstItem = cellIndex === 0

                            const isLastItem =
                                row.cells.length - 1 !== cellIndex

                            return cell.isProgressBar ? (
                                <TableCell
                                    className={`${styles.progressBarWidth} ${
                                        isFirstItem ? styles.firstCell : ''
                                    } ${
                                        isLastItem
                                            ? styles.progressBarPadding
                                            : ''
                                    }`}
                                    key={cell.text.toString() || uuidv4()}
                                >
                                    <GenomateScoreCell
                                        cell={cell}
                                        children={children}
                                    />
                                </TableCell>
                            ) : (
                                <TableCell
                                    key={cell.text.toString() || uuidv4()}
                                    className={
                                        isFirstItem
                                            ? `${styles.firstCell} ${variantNoNav}`
                                            : ''
                                    }
                                >
                                    <SimpleCell cell={cell} />
                                </TableCell>
                            )
                        })}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    )
}
export default TableWithGenomateScoreColumn
